// AddCompositionDialog.tsx
// Dialog for adding components to a composite add-on
// Part of Phase 2: Composite Add-ons Frontend

import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { adminService, AddOnService } from '../../services/adminService';

interface AddCompositionDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (childAddOnId: number, quantity: number) => Promise<void>;
  existingCompositions: number[]; // IDs der bereits hinzugefügten Add-ons
  parentAddOnId?: number; // ID des Composite Add-ons (zum Filtern)
}

export const AddCompositionDialog: React.FC<AddCompositionDialogProps> = ({
  open,
  onClose,
  onAdd,
  existingCompositions,
  parentAddOnId
}) => {
  const [availableAddOns, setAvailableAddOns] = useState<AddOnService[]>([]);
  const [selectedAddOnId, setSelectedAddOnId] = useState<number | ''>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lade verfügbare Add-ons (nur nicht-Composites, nicht bereits hinzugefügt)
  useEffect(() => {
    const fetchAvailableAddOns = async () => {
      try {
        const allAddOns = await adminService.getAllAddOns(false);
        const filtered = allAddOns.filter(
          (addon: AddOnService) =>
            !addon.isComposite && // Nur nicht-Composites
            addon.id !== parentAddOnId && // Nicht sich selbst
            !existingCompositions.includes(addon.id) // Nicht bereits hinzugefügt
        );
        setAvailableAddOns(filtered);
      } catch (err) {
        console.error('Failed to fetch add-ons:', err);
        setError('Fehler beim Laden der verfügbaren Add-ons');
      }
    };

    if (open) {
      fetchAvailableAddOns();
      // Reset form
      setSelectedAddOnId('');
      setQuantity(1);
      setError(null);
    }
  }, [open, existingCompositions, parentAddOnId]);

  const handleAdd = async () => {
    if (!selectedAddOnId) {
      setError('Bitte wähle ein Add-on aus');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAdd(selectedAddOnId as number, quantity);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Hinzufügen');
    } finally {
      setLoading(false);
    }
  };

  const selectedAddOn = availableAddOns.find(a => a.id === selectedAddOnId);

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
        }}
      >
        {/* Header */}
        <div style={{
          background: '#2d3748',
          padding: '1.5rem',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ margin: 0, color: 'white', fontSize: '20px' }}>
            Komponente hinzufügen
          </h3>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <XMarkIcon style={{ width: '24px', height: '24px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {error && (
            <div style={{
              background: '#fee',
              border: '1px solid #fcc',
              color: '#c33',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          {availableAddOns.length === 0 ? (
            <div style={{
              background: '#e3f2fd',
              border: '1px solid #90caf9',
              color: '#1976d2',
              padding: '1rem',
              borderRadius: '8px'
            }}>
              Keine verfügbaren Add-ons. Alle nicht-Composite Add-ons sind bereits hinzugefügt.
            </div>
          ) : (
            <>
              {/* Add-on Auswahl */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  Wähle Add-on *
                </label>
                <select
                  value={selectedAddOnId}
                  onChange={(e) => setSelectedAddOnId(Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    background: 'white'
                  }}
                  required
                >
                  <option value="">-- Bitte wählen --</option>
                  {availableAddOns.map((addon) => (
                    <option key={addon.id} value={addon.id}>
                      {addon.name} ({addon.price.toFixed(2)} €)
                    </option>
                  ))}
                </select>
              </div>

              {/* Menge */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  Menge *
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  min="1"
                  max="100"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  required
                />
              </div>

              {/* Preview */}
              {selectedAddOn && (
                <div style={{
                  background: '#f5f5f5',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Einzelpreis: {selectedAddOn.price.toFixed(2)} €
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                    Gesamt: {(selectedAddOn.price * quantity).toFixed(2)} €
                  </div>
                </div>
              )}

              {/* Hinweis */}
              <div style={{
                background: '#e3f2fd',
                border: '1px solid #90caf9',
                color: '#1976d2',
                padding: '0.75rem',
                borderRadius: '8px',
                fontSize: '12px'
              }}>
                <strong>Hinweis:</strong> Nur nicht-Composite Add-ons können als Komponenten verwendet werden.
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '1rem'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              background: 'white',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleAdd}
            disabled={loading || !selectedAddOnId || availableAddOns.length === 0}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: (loading || !selectedAddOnId || availableAddOns.length === 0) ? '#ccc' : '#007bff',
              color: 'white',
              borderRadius: '8px',
              cursor: (loading || !selectedAddOnId || availableAddOns.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            {loading ? 'Hinzufügen...' : 'Hinzufügen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCompositionDialog;
